import React from "react";
import { useNavigate } from "react-router-dom";
import { useDashboardFilter } from "../../context/DashboardFilterContext";
import {
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  DollarOutlined,
  MedicineBoxOutlined,
  SoundOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { useGetDashboardMetricsQuery } from "../../services/dashboardApi";
import type { DashboardMetric } from "../../services/dashboardApi";

export interface StatItem {
  key: string;
  title: string;
  value: string;
  change: number;
  changeType: "up" | "down";
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
}

const KEY_TO_ICON: Record<string, { icon: React.ReactNode; iconColor: string; iconBg: string }> = {
  registered: { icon: <CarOutlined />, iconColor: "text-amber-600", iconBg: "bg-amber-100 dark:bg-amber-900/30" },
  completed: { icon: <CheckCircleOutlined />, iconColor: "text-green-600", iconBg: "bg-green-100 dark:bg-green-900/30" },
  rejected: { icon: <CloseCircleOutlined />, iconColor: "text-red-600", iconBg: "bg-red-100 dark:bg-red-900/30" },
  cancelled: { icon: <ExclamationCircleOutlined />, iconColor: "text-amber-600", iconBg: "bg-amber-100 dark:bg-amber-900/30" },
  scheduled: { icon: <CalendarOutlined />, iconColor: "text-red-600", iconBg: "bg-red-100 dark:bg-red-900/30" },
  active_customer: { icon: <TeamOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  active_customers: { icon: <TeamOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  active_drivers: { icon: <TeamOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  airport_pickup: { icon: <SafetyCertificateOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  airport_pickups: { icon: <SafetyCertificateOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  airport: { icon: <SafetyCertificateOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  revenue: { icon: <DollarOutlined />, iconColor: "text-amber-600", iconBg: "bg-amber-100 dark:bg-amber-900/30" },
  total_spent: { icon: <DollarOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  earnings: { icon: <DollarOutlined />, iconColor: "text-amber-600", iconBg: "bg-amber-100 dark:bg-amber-900/30" },
  escalations: { icon: <MedicineBoxOutlined />, iconColor: "text-red-600", iconBg: "bg-red-100 dark:bg-red-900/30" },
  sos: { icon: <SoundOutlined />, iconColor: "text-red-600", iconBg: "bg-red-100 dark:bg-red-900/30" },
  total: { icon: <CarOutlined />, iconColor: "text-amber-600", iconBg: "bg-amber-100 dark:bg-amber-900/30" },
  total_trips: { icon: <CarOutlined />, iconColor: "text-amber-600", iconBg: "bg-amber-100 dark:bg-amber-900/30" },
  fleet_rides: { icon: <SafetyCertificateOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  active_hours: { icon: <CalendarOutlined />, iconColor: "text-blue-600", iconBg: "bg-blue-100 dark:bg-blue-900/30" },
  self_owned: { icon: <CarOutlined />, iconColor: "text-green-600", iconBg: "bg-green-100 dark:bg-green-900/30" },
};

const DEFAULT_ICON = { icon: <CarOutlined />, iconColor: "text-gray-600", iconBg: "bg-gray-100 dark:bg-gray-800" };

function metricToStatItem(m: DashboardMetric): StatItem {
  const iconConfig = KEY_TO_ICON[m.key] ?? DEFAULT_ICON;
  return {
    key: m.key,
    title: m.title,
    value: m.value,
    change: m.change,
    changeType: m.changeType,
    ...iconConfig,
  };
}

const NAV_KEYS = [
  "registered", "completed", "cancelled", "scheduled", "total_trips",
  "active_customer", "active_customers", "airport_pickup", "airport_pickups", "airport",
  "revenue", "total_spent", "sos",
] as const;

function getMetricNavUrl(key: string): string | null {
  switch (key) {
    case "registered":
    case "total_trips":
      return "/booking";
    case "completed":
      return "/booking?booking_status=COMPLETED";
    case "cancelled":
      return "/booking?booking_status=CANCELLED";
    case "scheduled":
      return "/booking?scheduled_time=LATER";
    case "active_customer":
    case "active_customers":
      return "/customer";
    case "airport_pickup":
    case "airport_pickups":
    case "airport":
      return "/booking?isAirportPickups=true";
    case "revenue":
    case "total_spent":
      return "/booking?booking_status=COMPLETED";
    case "complaints":
    case "sos":
      return "/safety";
    default:
      return null;
  }
}

const DashboardStats: React.FC = () => {
  const navigate = useNavigate();
  const { userType, timePeriod } = useDashboardFilter();

  const { data, isLoading } = useGetDashboardMetricsQuery({
    tab: userType,
    timePeriod,
  });

  const statsData: StatItem[] = data?.data?.metrics
    ? data.data.metrics.map(metricToStatItem)
    : [];

  if (isLoading && statsData.length === 0) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="dashboard-card animate-pulse">
            <div className="h-12 w-12 rounded-xl bg-gray-200 dark:bg-gray-700" />
            <div className="mt-4 h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="mt-2 h-8 w-16 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
      {statsData.map((stat) => {
        const navUrl = NAV_KEYS.includes(stat.key as (typeof NAV_KEYS)[number])
          ? getMetricNavUrl(stat.key)
          : null;
        const isClickable = !!navUrl;
        return (
        <div
          key={stat.title}
          className={`dashboard-card group ${isClickable ? "cursor-pointer outline-none focus:outline-none focus-visible:outline-none hover:!border-gray-200/80 dark:hover:!border-gray-700/80" : ""}`}
          role={isClickable ? "button" : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onClick={isClickable ? () => navigate(navUrl!) : undefined}
          onKeyDown={isClickable ? (e) => e.key === "Enter" && navigate(navUrl!) : undefined}
        >
          <div className="flex items-start justify-between">
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-lg transition-transform duration-200 group-hover:scale-105 ${stat.iconBg} ${stat.iconColor}`}
            >
              {stat.icon}
            </div>
            <div
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
                stat.changeType === "up"
                  ? "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400"
              }`}
            >
              {stat.changeType === "up" ? (
                <ArrowUpOutlined className="text-[10px]" />
              ) : (
                <ArrowDownOutlined className="text-[10px]" />
              )}
              <span>{Math.abs(stat.change)}%</span>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {stat.title}
            </p>
            <p className="mt-1 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              {stat.value}
            </p>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default DashboardStats;
